import { z } from 'zod'
import { pence, type Pence } from './money.ts'

/**
 * Admin-adjustable settings (Richard, 2026-09-25: every open question is a
 * setting with a sensible default). Quotes snapshot these at approval, so a
 * change never alters an existing quote.
 */

export const FREQUENCIES = ['weekly', 'fortnightly', 'monthly', 'quarterly', 'annually'] as const
export type Frequency = (typeof FREQUENCIES)[number]

export const MONTHLY_CONVERSIONS = ['52_12', '4_33', '4'] as const
export type MonthlyConversion = (typeof MONTHLY_CONVERSIONS)[number]

const text = (max: number) => z.string().trim().max(max)
const optionalEmail = z.union([z.literal(''), z.email().max(254)])
const optionalUrl = z.union([z.literal(''), z.url().max(300)])

const company = z.object({
  tradingName: text(120).min(1),
  legalName: text(160),
  companyNumber: text(20),
  vatNumber: text(20),
  registeredAddress: text(400),
  phone: text(40),
  email: optionalEmail,
  website: optionalUrl,
})

const tax = z.object({
  vatRegistered: z.boolean(),
  rounding: z.enum(['per_line', 'per_total']),
  pricesEnteredAs: z.enum(['net']),
})

const quotes = z.object({
  validityDays: z.number().int().min(1).max(365),
  referencePrefix: z
    .string()
    .regex(/^[A-Z0-9-]{1,10}$/, 'Use 1–10 capital letters, numbers or dashes'),
  nextNumber: z.number().int().min(1).max(9_999_999),
  monthlyConversion: z.enum(MONTHLY_CONVERSIONS),
  showMonthlyEquivalentToClient: z.boolean(),
  paymentTerms: text(2000),
})

const approvals = z.object({
  mode: z.enum(['always', 'thresholds', 'never']),
  /** Used when mode = thresholds: any trigger requires approval. 0 disables that trigger. */
  valueAbovePence: z.number().int().min(0),
  marginBelowBp: z.number().int().min(0).max(10_000),
  discountAboveBp: z.number().int().min(0).max(10_000),
  allowSelfApproval: z.boolean(),
  approverRoles: z.array(z.enum(['owner', 'commercial_manager'])).min(1),
})

const proposal = z.object({
  /** Plain text. A line starting "# " is a heading; blank lines separate paragraphs. */
  aboutUs: text(12000),
  scopeHeading: text(80).min(1),
  otherServicesHeading: text(80),
  otherServices: z.array(text(80).min(1)).max(30),
  sectors: z.array(text(80).min(1)).max(30),
  /** Agreement wording. Merge fields: {{date}}, {{client_name}}, {{client_company}}, {{client_address}}, {{client_business}}, {{hcd_legal_name}}, {{hcd_company_number}}, {{hcd_address}}. */
  agreementText: text(12000),
  declarationText: text(500).min(1),
  acceptanceNote: text(1000),
})

const email = z.object({
  senderName: text(80).min(1),
  fromAddress: optionalEmail,
  replyTo: optionalEmail,
})

export const SETTINGS = {
  company: {
    label: 'Company details',
    schema: company,
    defaults: {
      tradingName: 'HCD Cleaning',
      legalName: 'Hamilton Commercial & Domestic Cleaning Ltd',
      companyNumber: '06612754',
      vatNumber: '941 3389 19',
      registeredAddress:
        'Bestwood Hall, Bestwood Drive, Clay Cross, Chesterfield, Derbyshire S45 9GA',
      phone: '01246 287559',
      email: '',
      website: '',
    } satisfies z.infer<typeof company>,
  },
  tax: {
    label: 'Tax and VAT',
    schema: tax,
    defaults: {
      vatRegistered: true,
      rounding: 'per_line',
      pricesEnteredAs: 'net',
    } satisfies z.infer<typeof tax>,
  },
  quotes: {
    label: 'Quotes',
    schema: quotes,
    defaults: {
      validityDays: 30,
      referencePrefix: 'Q',
      nextNumber: 1001,
      monthlyConversion: '52_12',
      showMonthlyEquivalentToClient: false,
      paymentTerms: 'Invoices are payable within 30 days of the invoice date.',
    } satisfies z.infer<typeof quotes>,
  },
  approvals: {
    label: 'Approvals',
    schema: approvals,
    defaults: {
      mode: 'never',
      valueAbovePence: 0,
      marginBelowBp: 0,
      discountAboveBp: 0,
      allowSelfApproval: false,
      approverRoles: ['owner', 'commercial_manager'],
    } satisfies z.infer<typeof approvals>,
  },
  proposal: {
    label: 'Proposal content',
    schema: proposal,
    defaults: {
      aboutUs: [
        '# Experience',
        "Since day one, our knowledge and expertise in commercial cleaning has been the lifeline of our business. When you partner with HCD Cleaning you can rest assured that your cleaning requirements will be scrutinised by one of the industry's most experienced cleaning teams.",
        '',
        '# Integrity',
        'Each project brings unique challenges and requires careful attention to detail. We work hand in hand with our clients to ensure their distinctive vision becomes a reality. Our ultimate goal is to deliver a first class cleaning experience we can be proud of. Our team will provide the highest quality services at the best possible cost.',
        '',
        '# Relationships',
        'At HCD Cleaning our focus is on building a reputation for quality, customer-valued cleaning practices. Our past clients are the foundation for fostering a strong business in the future.',
        '',
        '# Values',
        'Committed to customer service. Work efficiently, offering value to our customers who are at the heart of everything we do. Our work is thorough, with attention to detail making a real difference. We employ great people.',
        '',
        '# Our services',
        'HCD Cleaning was founded on the premise that our customers come first. Our philosophy is simple: we aim to exceed customer expectations. We work hand in hand with our clients to provide the best possible cleaning service, and we will work with you to set up a tailored cleaning plan to meet the specific needs of your business. We use virucidal cleaning protocols to keep your premises safe from bacteria, helping maintain a healthy working environment for your employees and reduce absence among your staff.',
        '',
        '# Investments',
        'We invest in our people with ongoing training, support and incentives, which creates a culture of engaged and happy employees who do their best for our customers. We invest in the latest technologies, such as SmartTask workforce and scheduling software, which keeps our staff informed of their individual safety and helps them follow bespoke cleaning schedules for your premises. This reassures you that our cleaning teams maintain the expected standard of hygiene and cleaning at your premises.',
      ].join('\n'),
      scopeHeading: 'Services',
      otherServicesHeading: 'Other services we offer',
      otherServices: [
        'Daily cleaning services',
        'Weekly cleaning services',
        'Property management',
        'Laundry services',
        'Carpet cleaning',
        'Ozone room sanitation',
      ],
      sectors: [
        'Commercial and office',
        'Education',
        'Short-term lets',
        'Industrial and manufacturing',
        'Medical',
        'Residential',
        'Restaurant and hospitality',
      ],
      agreementText: [
        'THIS AGREEMENT is made on {{date}}.',
        '',
        '# Parties',
        '(1) {{hcd_legal_name}}, a company incorporated in England and Wales under number {{hcd_company_number}}, whose registered office is at {{hcd_address}} (the Supplier); and',
        '(2) {{client_name}} of {{client_company}}, whose registered office is at {{client_address}} (the Customer),',
        'each of the Supplier and the Customer being a party and together the parties.',
        '',
        '# Background',
        'A. The Supplier conducts the business of supplying cleaning and property management services to other businesses and individuals.',
        'B. The Customer conducts the business of {{client_business}}.',
        'C. The parties have agreed that the Supplier shall supply services to the Customer on the terms set out in this Agreement and the attached terms and conditions.',
      ].join('\n'),
      declarationText: 'I have read the attached terms and conditions.',
      acceptanceNote:
        'By signing you accept this proposal and the attached terms and conditions on behalf of the organisation named above.',
    } satisfies z.infer<typeof proposal>,
  },
  email: {
    label: 'Email',
    schema: email,
    defaults: { senderName: 'HCD Cleaning', fromAddress: '', replyTo: '' } satisfies z.infer<
      typeof email
    >,
  },
} as const

export type SettingsSection = keyof typeof SETTINGS
export const SETTINGS_SECTIONS = Object.keys(SETTINGS) as SettingsSection[]
export type SettingsValue<K extends SettingsSection> = z.infer<(typeof SETTINGS)[K]['schema']>

export const isSettingsSection = (s: string): s is SettingsSection => s in SETTINGS

/**
 * Read a stored section: stored fields override defaults; anything unreadable
 * falls back to the default for that field rather than breaking the app.
 */
export function parseSection<K extends SettingsSection>(key: K, stored: unknown): SettingsValue<K> {
  const def = SETTINGS[key]
  const merged: Record<string, unknown> = { ...def.defaults }
  if (stored && typeof stored === 'object') {
    for (const [field, value] of Object.entries(stored)) {
      if (!(field in def.defaults)) continue
      const candidate = { ...merged, [field]: value }
      if (def.schema.safeParse(candidate).success) merged[field] = value
    }
  }
  return def.schema.parse(merged) as SettingsValue<K>
}

/** Monthly equivalent of a recurring price, for internal reporting. Nearest penny. */
export function monthlyEquivalent(
  amount: Pence,
  frequency: Frequency,
  conversion: MonthlyConversion,
): Pence {
  const weeksPerMonth: Record<MonthlyConversion, [number, number]> = {
    '52_12': [52, 12],
    '4_33': [433, 100],
    '4': [4, 1],
  }
  const [num, den] = weeksPerMonth[conversion]
  const ratio: Record<Frequency, [number, number]> = {
    weekly: [num, den],
    fortnightly: [num, den * 2],
    monthly: [1, 1],
    quarterly: [1, 3],
    annually: [1, 12],
  }
  const [n, d] = ratio[frequency]
  return pence(Math.floor((amount * n * 2 + d) / (2 * d)))
}
